import { Link } from "react-router-dom"
import { ArrowLeft, Shield, FileText, Lock, Mail } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { useRef, useMemo } from 'react'

// Quick navigation removed per request

export default function PrivacyPolicy() {
  const contentRef = useRef<HTMLDivElement>(null)
  const lastUpdated = 'January 22, 2026'
  const plainTextForRead = `Privacy Policy Travel Warehouse Inc. is committed to safeguarding privacy...`
  const readTime = useMemo(() => Math.ceil(plainTextForRead.split(/\s+/).length / 200), [])

  return (
    <div className="min-h-screen bg-white pt-24 md:pt-32">
      {/* Simple Header */}
      <div className="border-b border-gray-200">
        <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <Link to="/">
            <Button variant="ghost" className="text-gray-700 hover:bg-gray-100 mb-6 -ml-2 group">
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Home
            </Button>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Privacy Policy</h1>
          <p className="text-gray-600 mt-2">Updated {new Date(lastUpdated).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} • {readTime} min read</p>
        </div>
      </div>

      {/* Content Section */}
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 scroll-smooth">
        {/* Main Content */}
        <div>
            {/* Info Banner */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-gray-200 rounded-lg flex-shrink-0">
                  <Lock className="h-5 w-5 text-gray-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Your Privacy Matters</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    This policy explains how Travel Warehouse, Inc. collects, uses, and protects your personal information. 
                    We are committed to safeguarding your data and maintaining your trust.
                  </p>
                </div>
              </div>
            </div>

            {/* Document Content Card */}
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 overflow-hidden border border-gray-100">
              <div ref={contentRef} className="p-5 sm:p-8 md:p-10 lg:p-12">
                <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none
                  prose-headings:text-gray-900 prose-headings:font-bold prose-headings:scroll-mt-24
                  prose-h1:text-2xl prose-h1:sm:text-3xl prose-h1:mb-6 prose-h1:sm:mb-8 prose-h1:pb-4 prose-h1:border-b prose-h1:border-gray-200
                  prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:mt-8 prose-h2:sm:mt-12 prose-h2:mb-4 prose-h2:text-gray-900
                  prose-h3:text-lg prose-h3:sm:text-xl prose-h3:mt-6 prose-h3:sm:mt-8 prose-h3:mb-3 prose-h3:text-gray-800
                  prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-li:text-gray-700 prose-li:mb-2
                  prose-ul:my-4 prose-ol:my-4
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-a:text-blue-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                  prose-hr:border-gray-200 prose-hr:my-8 prose-hr:sm:my-10
                  prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:not-italic prose-blockquote:rounded-r-lg
                  prose-code:bg-gray-100 prose-code:px-2 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-normal
                  prose-table:border-collapse prose-th:bg-gray-50 prose-th:border prose-th:border-gray-200 prose-th:px-4 prose-th:py-2
                  prose-td:border prose-td:border-gray-200 prose-td:px-4 prose-td:py-2
                ">
                  <h1>Privacy Policy</h1>
                  <p>Travel Warehouse Inc. ("we", "us", or "our") is committed to safeguarding privacy and ensuring that the personal information of its clients ("you" or "your") is protected. This Privacy Policy describes how we use the personal information we collect from you through our website when you view or interact with the contents thereof and when you interact with us.</p>

                  <h2>Section 1. How we collect personal information about you</h2>
                  <h3>1.1 Information You Provide</h3>
                  <p>You may provide us with your personal information in a number of ways, such as, but not limited to, the following:</p>
                  <ul>
                    <li>Providing personal information via our website by filling out forms</li>
                    <li>Communicating with us</li>
                    <li>Reporting a problem in using our website</li>
                    <li>Requesting support for any of our services</li>
                  </ul>
                  <h3>1.2 Information From Vendors And Suppliers</h3>
                  <p>We work closely with third-party vendors and/or suppliers and may receive personal information about you from them. Depending on the relationship and activity, they will provide us with certain personal information that will help us understand how you interact with us so we can tailor our marketing and provide better and more personalized services to you. In some cases, third-parties address technical or logistical issues, prevent fraud, or keep our community safe from security threats.</p>
                  <h3>1.3 Publicly Available Information</h3>
                  <p>We may also receive and display information and content which you make publicly available on your social media account(s) when you post using one of our promoted hashtags or when you interact with any contents on our website.</p>
                  <h3>1.4 Information From Key Partners</h3>
                  <p>Some of our key partners, with whom we receive personal information from, may share data, including, but not limited to:</p>
                  <ul>
                    <li>Event partners and sponsors</li>
                    <li>Media and digital content partners</li>
                    <li>Electronic commerce platforms and services</li>
                  </ul>

                  <h2>Section 2. Personal information we collect and how we use it</h2>
                  <table>
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Sources</th>
                        <th>Purposes</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Personal and online identifiers</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From our partners and sponsors</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                            <li>Personalization of services</li>
                            <li>Customer support</li>
                            <li>Marketing and targeted advertising</li>
                            <li>Security and fraud prevention</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Financial account information</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From our payment providers</li>
                            <li>From our electronic commerce vendors</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide services</li>
                            <li>Sell merchandise</li>
                            <li>Support commerce-related content</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Customer profile information</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From our partners</li>
                            <li>From our third-party data providers</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                            <li>Personalization of services</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Transactional information</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From our customer support partners</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                            <li>Customer support analytics</li>
                            <li>Research and development</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Communications with you</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Online activity information</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From cookies and similar technologies</li>
                            <li>From our partners and sponsors</li>
                            <li>From our third-party data providers</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                            <li>Personalization of services</li>
                            <li>Direct marketing</li>
                            <li>Targeted advertising</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Non-precise geolocation information</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From cookies and similar technologies</li>
                            <li>From our partners and sponsors</li>
                            <li>From our technology service providers</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve service</li>
                            <li>Personalization of services</li>
                            <li>Fraud and cybersecurity</li>
                            <li>Direct marketing</li>
                            <li>Targeted advertising</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Sensory information</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From our technology service providers</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                            <li>Personalization of services</li>
                            <li>Enhancing live event experiences</li>
                            <li>Research and development</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Inferences about characteristics and preferences</td>
                        <td>
                          <ul>
                            <li>From our third-party data providers</li>
                            <li>From our partners and sponsors</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                            <li>Personalization of services</li>
                            <li>Direct marketing</li>
                            <li>Targeted advertising analytics</li>
                          </ul>
                        </td>
                      </tr>
                      <tr>
                        <td>Other information linked to the above</td>
                        <td>
                          <ul>
                            <li>Directly from you</li>
                            <li>From our third-party data providers</li>
                            <li>From our partners and sponsors</li>
                          </ul>
                        </td>
                        <td>
                          <ul>
                            <li>Provide and improve services</li>
                            <li>Personalization of services</li>
                          </ul>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <h2>Section 3. Disclosure of personal information</h2>
                  <p>We share your data with third-parties. Some of these disclosures may constitute a sale of data under relevant and applicable laws of the Republic of the Philippines. Accordingly, we offer the option for you to opt out of the sale of your personal information.</p>

                  <h2>Section 4. Marketing and advertising</h2>
                  <ul>
                    <li>We use your personal information to tailor our marketing and advertising efforts, including sharing with third-party advertising networks and social media companies.</li>
                    <li>When we provide data for targeted advertising, we do not provide your name, financial information, or any sensitive personal information.</li>
                    <li>We may use personal information to enable us to display advertisements to target audiences.</li>
                    <li>We are not responsible for the effectiveness of, or compliance with, any third-parties’ opt-out options or programs, or the accuracy of their statements.</li>
                    <li>The use of information you share on your social media account(s) is governed by the social media providers’ privacy policies.</li>
                  </ul>

                  <h2>Section 5. Cookies and similar technologies</h2>
                  <p>Our website uses cookies to help recognize you and provide a good user experience. We also use cookies and similar technologies to remember preferences, honor settings, enable sign-in, provide interest-based advertising, combat fraud, analyze performance, and fulfill other purposes described in this Privacy Policy.</p>

                  <h2>Section 6. Retention, disclosure, and transfer of your personal information</h2>
                  <ul>
                    <li>We will use your personal information for as long as necessary to satisfy the purposes for which it was collected or to comply with applicable legal requirements.</li>
                    <li>Personal information used to provide a personalized experience will be kept for a duration permitted by applicable laws.</li>
                    <li>We use appropriate measures to keep your personal information confidential and secure, but protections do not apply to information you choose to share in public areas.</li>
                    <li>Your personal information will be processed by authorized staff or agents on a need-to-know basis.</li>
                    <li>We use reasonable security measures to prevent unauthorized access; transmission over the Internet is not completely secure.</li>
                    <li>It is important that you also play an active role in keeping your personal information safe and secure.</li>
                    <li>Use any privacy settings or controls we provide on our website.</li>
                  </ul>

                  <h2>Section 7. Updates</h2>
                  <p>Any updates we may make to our Privacy Policy will be posted on our website. We may also provide a website notice for any material change. If you do not agree to these updates or changes, feel free to discontinue using our website.</p>

                  <h2>Section 8. General provisions</h2>
                  <ul>
                    <li>You confirm the information you give us is complete and accurate; failure to do so may result in our inability to provide requested products and services, and we will not be liable for resulting damages.</li>
                    <li>You may not assign, sub-license, or transfer any rights under our Privacy Policy.</li>
                    <li>If any provision is found invalid, the remaining provisions remain in full force and effect.</li>
                    <li>Failure to exercise any right or remedy does not constitute a waiver; any waiver must be in writing and signed by us.</li>
                    <li>The laws of the Republic of the Philippines apply to this Privacy Policy and your use of our website.</li>
                    <li>Courts of the Republic of the Philippines—particularly a trial court in Pasig City, Metro Manila—have exclusive jurisdiction over claims relating to our website or this Privacy Policy.</li>
                  </ul>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-5 sm:px-8 md:px-10 lg:px-12 py-5 sm:py-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-sm text-gray-600">
                    <p><strong>Document Version:</strong> Current</p>
                    <p><strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <Shield className="h-4 w-4 text-gray-600" />
                    <span>Your data is protected</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/terms-of-service">
                <Button variant="outline" className="w-full sm:w-auto gap-2 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-200">
                  <FileText className="h-4 w-4" />
                  View Terms of Service
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="w-full sm:w-auto gap-2 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-200">
                  <Mail className="h-4 w-4" />
                  Contact Us
                </Button>
              </Link>
            </div>
        </div>
      </div>
    </div>
  )
}
